import { CustomFormatPipe } from 'src/shared/pipes';
import { abbreviateNumber } from 'src/shared/utility/formats';

import { IIssueDetailFundamentalsResponse } from '../responses/issue-detail-fundamentals.response';

export interface IIssueDetailFundamentals {
  // Id
  id: string;
  // IssueID
  symbol: string;
  // Sector
  sector: string;
  // Cierre
  closePrior: number;
  // Apertura
  openPrice: number;
  // Mínimo del dia
  lowPrice: number;
  // Máximo del dia
  highPrice: number;
  // Mínimo 52 semanas
  fiftyTwoWeekLowPrice: number;
  // Máximo 52 semanas
  fiftyTwoWeekHighPrice: number;
  // Volumen
  cumulativeVolume: number;
  // Precio / Utilidad
  peRatio: number;
  // Rentabilidad por dividendo
  dividendYield: number;
  // Utilidad por acción
  earningsPerShare: number;
  // Capitalización bursátil
  marketCap: number;
  // Dividendo
  dividend: number;
  // Acciones de circulación
  sharesOutstanding: number;
  // Valor en libros / acción
  bookValuePerShare: number;
  // Flujo de efectivo por acción
  cashFlowPerShare: number;
  // Utilidad operativa
  operatingIncome: number;
  // Precio / Valor en libros
  pbRatio: number;
  // Retorno sobre capital
  roe: number;
  // Media móvil volumen 10 días
  volumeMovingAverage10Day: number;
  // Media móvil volumen 25 días
  volumeMovingAverage25Day: number;
  volumeMovingAverage50Day: number;
  // Media móvil precio 50 días
  priceMovingAverage50Day: number;
  // Media móvil precio 150 días
  priceMovingAverage150Day: number;
  // Media móvil precio 150 días
  priceMovingAverage200Day: number;
}

type FormatType = 'currency' | 'number' | 'percentage' | 'ratio';
export class IssueDetailFundamentalsViewModel
  implements IIssueDetailFundamentals
{
  public id: string;
  public symbol: string;
  public sector: string;
  public closePrior: number;
  public openPrice: number;
  public lowPrice: number;
  public highPrice: number;
  public fiftyTwoWeekLowPrice: number;
  public fiftyTwoWeekHighPrice: number;
  public cumulativeVolume: number;
  public peRatio: number;
  public dividendYield: number;
  public earningsPerShare: number;
  public marketCap: number;
  public dividend: number;
  public sharesOutstanding: number;
  public bookValuePerShare: number;
  public cashFlowPerShare: number;
  public operatingIncome: number;
  public pbRatio: number;
  public roe: number;
  public volumeMovingAverage10Day: number;
  public volumeMovingAverage25Day: number;
  public volumeMovingAverage50Day: number;
  public priceMovingAverage50Day: number;
  public priceMovingAverage150Day: number;
  public priceMovingAverage200Day: number;

  private pipe: CustomFormatPipe = new CustomFormatPipe();
  private formatDefinitions: Record<FormatType, string> = {
    currency: '$0,0.00',
    number: '0,0.00',
    percentage: '0,0.00[00]%',
    ratio: '0,00.00',
  };

  static fromJSON(
    json: IIssueDetailFundamentalsResponse,
  ): IssueDetailFundamentalsViewModel {
    return new IssueDetailFundamentalsViewModel({
      id: json.id,
      symbol: json.symbol,
      sector: json.sector,
      closePrior: json.close_prior,
      openPrice: json.open_price,
      lowPrice: json.low_price,
      highPrice: json.high_price,
      fiftyTwoWeekLowPrice: json.fifty_two_week_low_price,
      fiftyTwoWeekHighPrice: json.fifty_two_week_high_price,
      cumulativeVolume: json.cumulative_volume,
      peRatio: json.pe_ratio,
      dividendYield: json.dividend_yield,
      earningsPerShare: json.earnings_per_share,
      marketCap: json.market_cap,
      dividend: json.dividend,
      sharesOutstanding: json.shares_outstanding,
      bookValuePerShare: json.book_value_per_share,
      cashFlowPerShare: json.cash_flow_per_share,
      operatingIncome: json.operating_income,
      pbRatio: json.pb_ratio,
      roe: json.roe,
      volumeMovingAverage10Day: json.volume_moving_average10_day,
      volumeMovingAverage25Day: json.volume_moving_average25_day,
      volumeMovingAverage50Day: json.volume_moving_average50_day,
      priceMovingAverage50Day: json.price_moving_average50_day,
      priceMovingAverage150Day: json.price_moving_average150_day,
      priceMovingAverage200Day: json.price_moving_average200_day,
    });
  }
  static empty(): IssueDetailFundamentalsViewModel {
    return new IssueDetailFundamentalsViewModel({
      id: '',
      symbol: '',
      sector: '',
      closePrior: 0,
      openPrice: 0,
      lowPrice: 0,
      highPrice: 0,
      fiftyTwoWeekLowPrice: 0,
      fiftyTwoWeekHighPrice: 0,
      cumulativeVolume: 0,
      peRatio: 0,
      dividendYield: 0,
      earningsPerShare: 0,
      marketCap: 0,
      dividend: 0,
      sharesOutstanding: 0,
      bookValuePerShare: 0,
      cashFlowPerShare: 0,
      operatingIncome: 0,
      pbRatio: 0,
      roe: 0,
      volumeMovingAverage10Day: 0,
      volumeMovingAverage25Day: 0,
      volumeMovingAverage50Day: 0,
      priceMovingAverage50Day: 0,
      priceMovingAverage150Day: 0,
      priceMovingAverage200Day: 0,
    });
  }

  constructor(private fundamentals: IIssueDetailFundamentals) {
    /**
     * TODO: Find a generic and type safe way of doing this
     *
     * An example was found here https://codesandbox.io/s/primed-model-v8txy?file=/src/primed.ts
     */
    this.id = this.fundamentals.id;
    this.symbol = this.fundamentals.symbol;
    this.sector = this.fundamentals.sector;
    this.closePrior = this.fundamentals.closePrior;
    this.openPrice = this.fundamentals.openPrice;
    this.lowPrice = this.fundamentals.lowPrice;
    this.highPrice = this.fundamentals.highPrice;
    this.fiftyTwoWeekLowPrice = this.fundamentals.fiftyTwoWeekLowPrice;
    this.fiftyTwoWeekHighPrice = this.fundamentals.fiftyTwoWeekHighPrice;
    this.cumulativeVolume = this.fundamentals.cumulativeVolume;
    this.peRatio = this.fundamentals.peRatio;
    this.dividendYield = this.fundamentals.dividendYield;
    this.earningsPerShare = this.fundamentals.earningsPerShare;
    this.marketCap = this.fundamentals.marketCap;
    this.dividend = this.fundamentals.dividend;
    this.sharesOutstanding = this.fundamentals.sharesOutstanding;
    this.bookValuePerShare = this.fundamentals.bookValuePerShare;
    this.cashFlowPerShare = this.fundamentals.cashFlowPerShare;
    this.operatingIncome = this.fundamentals.operatingIncome;
    this.pbRatio = this.fundamentals.pbRatio;
    this.roe = this.fundamentals.roe;
    this.volumeMovingAverage10Day = this.fundamentals.volumeMovingAverage10Day;
    this.volumeMovingAverage25Day = this.fundamentals.volumeMovingAverage25Day;
    this.volumeMovingAverage50Day = this.fundamentals.volumeMovingAverage50Day;
    this.priceMovingAverage50Day = this.fundamentals.priceMovingAverage50Day;
    this.priceMovingAverage150Day = this.fundamentals.priceMovingAverage150Day;
    this.priceMovingAverage200Day = this.fundamentals.priceMovingAverage200Day;
  }

  public get closePriorFormatted() {
    return this.formatNumber(this.closePrior);
  }

  public get openPriceFormatted() {
    return this.formatNumber(this.openPrice);
  }

  public get lowPriceFormatted() {
    return this.formatNumber(this.lowPrice);
  }

  public get highPriceFormatted() {
    return this.formatNumber(this.highPrice);
  }

  public get fiftyTwoWeekLowPriceFormatted() {
    return this.formatNumber(this.fiftyTwoWeekLowPrice);
  }

  public get fiftyTwoWeekHighPriceFormatted() {
    return this.formatNumber(this.fiftyTwoWeekHighPrice);
  }

  public get cumulativeVolumeFormatted() {
    return this.formatNumber(this.cumulativeVolume, {
      type: 'number',
    });
  }

  public get peRatioFormatted() {
    return this.formatNumber(this.peRatio, { type: 'ratio' });
  }

  public get dividendYieldFormatted() {
    return this.formatNumber(this.peRatio, {
      type: 'percentage',
    });
  }

  public get earningsPerShareFormatted() {
    return this.formatNumber(this.earningsPerShare);
  }

  public get marketCapFormatted() {
    return `$${abbreviateNumber(this.marketCap, 2, 2)}`;
  }

  public get dividendFormatted() {
    return this.formatNumber(this.dividend);
  }

  public get sharesOutstandingFormatted() {
    return `$${abbreviateNumber(this.sharesOutstanding, 2, 2)}`;
  }

  public get bookValuePerShareFormatted() {
    return this.formatNumber(this.bookValuePerShare);
  }

  public get cashFlowPerShareFormatted() {
    return this.formatNumber(this.cashFlowPerShare);
  }

  public get operatingIncomeFormatted() {
    return `$${abbreviateNumber(this.operatingIncome, 2, 2)}`;
  }

  public get pbRatioFormatted() {
    return this.formatNumber(this.pbRatio, { type: 'ratio' });
  }

  public get roeFormatted() {
    return this.formatNumber(this.roe, {
      type: 'percentage',
    });
  }

  public get volumeMovingAverage10DayFormatted() {
    return this.formatNumber(this.volumeMovingAverage10Day, {
      type: 'number',
    });
  }

  public get volumeMovingAverage25DayFormatted() {
    return this.formatNumber(this.volumeMovingAverage25Day, {
      type: 'number',
    });
  }

  public get volumeMovingAverage50DayFormatted() {
    return this.formatNumber(this.volumeMovingAverage50Day, {
      type: 'number',
    });
  }

  public get priceMovingAverage50DayFormatted() {
    return this.formatNumber(this.priceMovingAverage50Day);
  }

  public get priceMovingAverage150DayFormatted() {
    return this.formatNumber(this.priceMovingAverage150Day);
  }

  public get priceMovingAverage200DayFormatted() {
    return this.formatNumber(this.priceMovingAverage200Day);
  }

  private formatNumber(
    value: number,
    options: {
      type: FormatType;
    } = {
      type: 'currency',
    },
  ) {
    if (typeof value !== 'number') {
      return '-';
    }
    const formatString = this.formatDefinitions[options.type];

    if (options.type === 'ratio') {
      /**
       * We could register a custom numeral.js formatter, but it
       * has given us trouble and we plan to stop using it.
       * Plus the format only consists of adding 'x', and we don't
       * need to un-format it to operate on it as number.
       *
       * Check {@link CustomFormatPipe} for details about deprecation.
       */
      return `${this.pipe.transform(value, formatString)}x`;
    }

    return this.pipe.transform(value, formatString);
  }
}
